<div class="bk-page sb">
  <header class="bk-page__head">
    <div class="bk-page__intro">
      <p class="bk-eyebrow">SMS</p>
      <h1 class="bk-page__title">Custom SMS</h1>
      <p class="bk-page__sub">
        Upload a spreadsheet and send every row its own SMS, with that row’s values merged into the message.
      </p>
    </div>

    @if (canStartOver()) {
      <div class="bk-page__actions">
        <button type="button" class="bk-btn bk-btn--ghost" (click)="askStartOver()">
          <span class="bk-sym" aria-hidden="true">restart_alt</span>
          Start over
        </button>
      </div>
    }
  </header>

  <!-- Where you are: done steps can be revisited, later ones open as you go -->
  <nav #stepsNav class="sb-steps" aria-label="Custom SMS steps">
    <ol class="sb-steps__list">
      @for (item of steps; track item.label; let i = $index) {
        <li
          class="sb-steps__item"
          [class.is-current]="i === store.step()"
          [class.is-done]="i < store.step() || (i === 4 && !!store.sent())"
        >
          <button
            type="button"
            class="sb-steps__btn"
            [disabled]="!canGo(i)"
            [attr.aria-current]="i === store.step() ? 'step' : null"
            [attr.aria-label]="stepLabel(i)"
            (click)="go(i)"
          >
            <span class="sb-steps__num" aria-hidden="true">
              @if (i < store.step()) {
                <span class="bk-sym">check</span>
              } @else {
                {{ i + 1 }}
              }
            </span>
            <span class="sb-steps__label">{{ item.label }}</span>
          </button>
        </li>
      }
    </ol>
  </nav>

  <p class="bk-sr-only" role="status" aria-live="polite">{{ liveMessage }}</p>

  @if (resumedInMemory) {
    <p class="sb-resumed">
      <span class="bk-sym" aria-hidden="true">history</span>
      Picked up where you left off. Nothing has been sent yet.
    </p>
  }

  <!-- A draft from an earlier visit, offered back before a new upload -->
  @if (draft && store.step() === 0 && !store.hasRows()) {
    <section class="bk-panel sb-draft" aria-labelledby="sb-draft-title">
      <span class="bk-sym sb-draft__icon" aria-hidden="true">draft</span>
      <div class="sb-draft__text">
        <h2 class="sb-draft__title" id="sb-draft-title">
          {{ draft.draft.subject || "Unnamed broadcast" }}
        </h2>
        <p class="sb-draft__meta">
          Unsent draft from {{ draftWhen() }}
          @if (draft.draft.fileName) {
            <span class="sb-draft__sep" aria-hidden="true">·</span>
            {{ draft.draft.fileName }}, {{ formatCount(draft.draft.rowCount) }}
            {{ draft.draft.rowCount === 1 ? "row" : "rows" }}
          }
        </p>
        @if (draft.draft.fileName && !draft.sheetKept) {
          <p class="sb-draft__hint">
            The message and settings are kept. You’ll upload {{ draft.draft.fileName }} again to carry on.
          </p>
        }
      </div>
      <div class="sb-draft__actions">
        <button type="button" class="bk-btn bk-btn--ghost" (click)="discardDraft()">Discard draft</button>
        <button type="button" class="bk-btn bk-btn--primary" (click)="resumeDraft()">Carry on</button>
      </div>
    </section>
  }

  <div class="sb-grid">
    <div #stepHost class="sb-main">
      @switch (store.step()) {
        @case (0) {
          <sb-upload-data (next)="next()" />
        }
        @case (1) {
          <sb-phone-column (next)="next()" (back)="back()" />
        }
        @case (2) {
          <sb-compose (next)="next()" (back)="back()" />
        }
        @case (3) {
          <sb-review (back)="back()" (sent)="onSent()" />
        }
        @case (4) {
          <sb-send-progress (restart)="startAnother()" />
        }
      }
    </div>

    <aside class="sb-aside" aria-label="Broadcast summary">
      <sb-summary (edit)="go($event)" />
    </aside>
  </div>
</div>

<app-prompt-card
  [openModal]="dialog !== null"
  [title]="dialogTitle"
  [buttonText]="dialogAction"
  [closeText]="dialogClose"
  (onClose)="closeDialog(false)"
  (onButtonClick)="closeDialog(true)"
>
  <p class="sb-dialog">{{ dialogBody }}</p>
</app-prompt-card>
